// @system — Server-rendered initial state (written by lib/@system/Ssr on the server, read here).
//
//   <div id="root" data-ssr="1">…HTML rendered by client/src/entry-server.jsx…</div>
//   <script type="application/json" id="app-initial-state">
//     { "url": "/blog", "page": { "kind": "blog", "key": "/blog", "data": { … }, "status": 200 } }
//   </script>
//
// One store, two writers. The server entry seeds it with `seedInitialState()` before
// renderToString; the browser parses the JSON block once. Pages read their payload inside a
// useState initialiser through `readInitialPage(kind, key)` so the first (hydrating) render is
// identical on both sides, then call `releaseInitialPage()` from their mount effect: from that
// moment the payload is gone, so a later client-side navigation back to the same URL fetches
// from the API as before and routing never shows stale server data. Reading is idempotent on
// purpose — development React (StrictMode) renders a mounting component twice and keeps the
// second pass, so a read-that-consumes would hand the second pass nothing and hydration would
// fail everywhere React is not the production build. `isServerRendered()` is the single gate
// the providers use to start from hydration-safe state (signed-out, default theme).
//
// The block is `type="application/json"`: inert, so the CSP script-src hash logic in
// server/src/lib/@system/Middleware/security.js is untouched.

export const INITIAL_STATE_ELEMENT_ID = 'app-initial-state'

/**
 * @typedef {object} InitialPage
 * @property {string} kind    page family the payload belongs to ('pricing' | 'blog' | 'blogPost' | …)
 * @property {string} key     URL the server rendered for (pathname; pathname + search when the page reads it)
 * @property {unknown} data   JSON-safe payload — exactly what the page would have fetched
 * @property {number} [status] HTTP status the payload implies (404 when a slug loader found nothing)
 */
/**
 * @typedef {object} InitialState
 * @property {string} [url]             pathname + search the server rendered
 * @property {InitialPage|null} [page]  page payload, null for routes without data (/, /auth)
 */

/** @type {{ parsed: boolean, seeded: boolean, state: InitialState|null, pageReleased: boolean }} */
const store = { parsed: false, seeded: false, state: null, pageReleased: false }

/**
 * Server entry only: install the state for the render that follows (replaces any previous one —
 * the bundle is shared by every request of the process and renderToString is synchronous).
 * @param {InitialState|null} state
 */
export function seedInitialState(state) {
  store.parsed = true
  store.seeded = true
  store.pageReleased = false
  store.state = state && typeof state === 'object' ? state : null
}

/**
 * Parse the data block once (null when absent or malformed).
 * @param {Document|null} [doc]
 * @returns {InitialState|null}
 */
export function readInitialState(doc = typeof document !== 'undefined' ? document : null) {
  if (store.parsed) return store.state
  store.parsed = true
  if (!doc) return null
  const el = doc.getElementById(INITIAL_STATE_ELEMENT_ID)
  if (!el || !el.textContent) return null
  try {
    const parsed = JSON.parse(el.textContent)
    store.state = parsed && typeof parsed === 'object' ? parsed : null
  } catch {
    store.state = null
  }
  return store.state
}

/**
 * The inlined page payload when it was rendered for exactly this page (kind + URL key) and has not
 * been released yet. Idempotent: call it from a useState initialiser as often as React renders the
 * mounting component; release it from the mount effect (see releaseInitialPage).
 * @template T
 * @param {string} kind
 * @param {string} key  URL the page is rendering — see currentPageKey()
 * @returns {T|null}
 */
export function readInitialPage(kind, key) {
  const s = readInitialState()
  if (!s?.page || store.pageReleased) return null
  if (s.page.kind !== kind || s.page.key !== key) return null
  return /** @type {T} */ (s.page.data)
}

/**
 * Forget the page payload: the page that rendered from it has mounted. A later mount of the same
 * URL (client-side navigation back) reads null and fetches from the API. Idempotent.
 */
export function releaseInitialPage() {
  store.pageReleased = true
}

/**
 * URL key of the current page as the server computes it: the pathname, or pathname + raw search.
 * During a server render (store seeded — there is no window in the Node bundle, and a jsdom test
 * window must not leak in either) it is the seeded URL, so a page's useState initialiser resolves
 * the same key on both sides.
 * @param {boolean} [withSearch=false]
 * @returns {string}
 */
export function currentPageKey(withSearch = false) {
  if (store.seeded || typeof window === 'undefined') {
    const url = typeof store.state?.url === 'string' ? store.state.url : ''
    return withSearch ? url : url.split('?')[0]
  }
  return withSearch ? `${window.location.pathname}${window.location.search}` : window.location.pathname
}

/**
 * True while rendering on the server, and in the browser when #root carries `data-ssr`
 * (the document was server-rendered and React is hydrating it). Providers start from the
 * state the server used so the first client render matches the HTML; documents served as the
 * plain shell keep today's behaviour and today's tests.
 * @param {Document|null} [doc]
 * @returns {boolean}
 */
export function isServerRendered(doc = typeof document !== 'undefined' ? document : null) {
  if (store.seeded) return true
  const root = doc?.getElementById('root')
  return !!root?.dataset?.ssr
}

/** Test helper: forget the parsed/seeded block. */
export function _resetInitialState() {
  store.parsed = false
  store.seeded = false
  store.state = null
  store.pageReleased = false
}
